/* store.tsx — the feedback-toolbar state container. */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  FeedbackContextValue,
  NewAutoTicketInput,
  NewTicketInput,
  SyncState,
  Ticket,
  ToastItem,
} from './types';
import { CSRF_HEADERS, JSON_HEADERS, newCommentId, newId, viewport } from './constants';
import {
  captureViewport,
  surfacePointToClientPoint,
  uploadScreenshot,
} from './screenshot';

const SYNC_DEBOUNCE_MS = 600;
const SYNC_POLL_MS = 30000;
const DEDUPE_WINDOW_MS = 24 * 60 * 60 * 1000;
const DEAD_CLICK_MS = 700;

class SyncHttpError extends Error {
  constructor(readonly status: number) {
    super(`sync request failed with HTTP ${status}`);
    this.name = 'SyncHttpError';
  }
}
function isUnauthorized(err: unknown): boolean {
  return err instanceof SyncHttpError && err.status === 401;
}

const Ctx = createContext<FeedbackContextValue | null>(null);

function loadJSON<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}
function saveJSON(key: string, v: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* Quota / private mode — ignore, in-memory state still works */
  }
}
function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Quota / private mode — ignore */
  }
}

export interface ProviderProps {
  route: string;
  routeLabel: string;
  syncUrl?: string;
  defaultAutoCapture: boolean;
  storageKey: string;
  screenshotUrl?: string;
  isZoeAdmin?: boolean;
  audienceKey?: string;
  // Extra headers for every sync request, re-read per call so a token that changes mid-session is picked up.
  authHeaders?: () => Record<string, string>;
  children: React.ReactNode;
}

export function FeedbackProvider({
  route,
  routeLabel,
  syncUrl,
  defaultAutoCapture,
  storageKey,
  screenshotUrl,
  isZoeAdmin = false,
  audienceKey,
  authHeaders,
  children,
}: ProviderProps) {
  const authHeadersRef = useRef(authHeaders);
  authHeadersRef.current = authHeaders;
  const withAuth = useCallback(
    (base: Record<string, string>): Record<string, string> => ({
      ...base,
      ...(authHeadersRef.current?.() ?? {}),
    }),
    [],
  );
  const LS_TICKETS = audienceKey ? `${storageKey}-${audienceKey}-tickets` : `${storageKey}-tickets`;
  const LS_REPORTER = `${storageKey}-reporter`;
  const LS_AUTOCAP = `${storageKey}-autocap`;
  const LS_SHOTS = `${storageKey}-screenshots`;
  const syncEnabled = Boolean(syncUrl);

  const filterAudienceTickets = useCallback(
    (items: Ticket[]) => (isZoeAdmin ? items : items.filter((ticket) => ticket.workflow?.review_only !== true)),
    [isZoeAdmin],
  );
  const [tickets, setTickets] = useState<Ticket[]>(() => filterAudienceTickets(loadJSON<Ticket[]>(LS_TICKETS, [])));
  const [reporter, _setReporter] = useState<string>(() => localStorage.getItem(LS_REPORTER) || '');
  const [autoCapture, _setAutoCapture] = useState<boolean>(() => {
    const v = localStorage.getItem(LS_AUTOCAP);
    return v === null ? defaultAutoCapture : v === 'true';
  });
  const [screenshots, _setScreenshots] = useState<boolean>(() => {
    return localStorage.getItem(LS_SHOTS) !== 'false';
  });
  const [commentMode, setCommentMode] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  // Mirror syncState in a ref so the debounced-write effect can read it WITHOUT listing syncState as a dependency.
  const syncStateRef = useRef<SyncState>('idle');
  syncStateRef.current = syncState;
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const priorTicketCacheKey = useRef(LS_TICKETS);
  const priorIsZoeAdmin = useRef(isZoeAdmin);

  // A browser can switch accounts without a hard reload.
  useEffect(() => {
    if (priorTicketCacheKey.current === LS_TICKETS && priorIsZoeAdmin.current === isZoeAdmin) return;
    priorTicketCacheKey.current = LS_TICKETS;
    priorIsZoeAdmin.current = isZoeAdmin;
    const scoped = filterAudienceTickets(loadJSON<Ticket[]>(LS_TICKETS, []));
    saveJSON(LS_TICKETS, scoped);
    setTickets(scoped);
  }, [LS_TICKETS, filterAudienceTickets, isZoeAdmin]);

  // Refs so the global (non-React) listeners always read latest values.
  const ticketsRef = useRef(tickets);
  useEffect(() => {
    ticketsRef.current = tickets;
  }, [tickets]);
  const routeRef = useRef({ route, routeLabel });
  useEffect(() => {
    routeRef.current = { route, routeLabel };
  }, [route, routeLabel]);

  const setReporter = useCallback(
    (r: string) => {
      _setReporter(r);
      safeSetItem(LS_REPORTER, r || '');
    },
    [LS_REPORTER],
  );
  const setAutoCapture = useCallback(
    (v: boolean) => {
      _setAutoCapture(v);
      safeSetItem(LS_AUTOCAP, String(!!v));
    },
    [LS_AUTOCAP],
  );
  const setScreenshots = useCallback(
    (v: boolean) => {
      _setScreenshots(v);
      safeSetItem(LS_SHOTS, String(!!v));
    },
    [LS_SHOTS],
  );

  // ─── toasts ─────────────────────────────────────────────────────────
  const pushToast = useCallback((t: Omit<ToastItem, 'id'> & { id?: string }): string => {
    const id = t.id || 't' + Date.now() + '-' + Math.random().toString(36).slice(2, 6);
    setToasts((prev) => [...prev, { ttl: 3200, ...t, id }]);
    return id;
  }, []);
  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ─── ticket actions ─────────────────────────────────────────────────
  const stamp = useCallback(
    () => ({
      route: routeRef.current.route,
      routeLabel: routeRef.current.routeLabel,
      url: window.location.href,
      viewport: viewport(),
      userAgent: navigator.userAgent,
      reporter: reporter || null,
    }),
    [reporter],
  );

  // Assigned below, once updateTicket exists.
  const attachScreenshotRef = useRef<(ticket: Ticket) => void>(() => {});

  const addTicket = useCallback(
    (entry: NewTicketInput): Ticket => {
      const now = new Date().toISOString();
      const base = stamp();
      const ticket: Ticket = {
        id: newId(),
        status: 'open',
        severity: 'medium',
        tag: 'comment',
        kind: 'note',
        source: 'manual',
        assignee: null,
        comments: [],
        ...base,
        ...entry,
        title: entry.title || (entry.note || '').slice(0, 60).trim() || (entry.tag ?? 'untitled'),
        createdAt: now,
        updatedAt: now,
      };
      setTickets((prev) => {
        const next = [ticket, ...prev];
        saveJSON(LS_TICKETS, next);
        return next;
      });
      // Fire-and-forget, AFTER the ticket is already in the store.
      attachScreenshotRef.current(ticket);
      return ticket;
    },
    [LS_TICKETS, stamp],
  );

  // Auto-capture insert with 24h dedupe by key → bumps occurrences instead of duplicating.
  const addAutoTicket = useCallback(
    (entry: NewAutoTicketInput): Ticket | null => {
      let result: Ticket | null = null;
      setTickets((prev) => {
        const enabled = (localStorage.getItem(LS_AUTOCAP) ?? String(defaultAutoCapture)) !== 'false';
        if (!enabled) {
          result = null;
          return prev;
        }
        const key = entry.dedupeKey;
        const cutoff = Date.now() - DEDUPE_WINDOW_MS;
        if (key) {
          const existing = prev.find(
            (t) => t.dedupeKey === key && new Date(t.createdAt).getTime() > cutoff,
          );
          if (existing) {
            result = existing;
            const next = prev.map((t) =>
              t.id === existing.id
                ? { ...t, occurrences: (t.occurrences || 1) + 1, updatedAt: new Date().toISOString() }
                : t,
            );
            saveJSON(LS_TICKETS, next);
            return next;
          }
        }
        const now = new Date().toISOString();
        const base = stamp();
        const ticket: Ticket = {
          id: newId(),
          status: 'open',
          severity: 'medium',
          tag: 'bug',
          kind: 'note',
          source: 'auto-error',
          assignee: null,
          comments: [],
          occurrences: 1,
          ...base,
          ...entry,
          title: entry.title || (entry.note || '').slice(0, 60).trim() || 'auto-captured',
          note: entry.note || '',
          createdAt: now,
          updatedAt: now,
        };
        result = ticket;
        const next = [ticket, ...prev];
        saveJSON(LS_TICKETS, next);
        return next;
      });
      return result;
    },
    [LS_AUTOCAP, LS_TICKETS, defaultAutoCapture, stamp],
  );

  const updateTicket = useCallback(
    (id: string, patch: Partial<Ticket>) => {
      setTickets((prev) => {
        const next = prev.map((t) =>
          t.id === id ? { ...t, ...patch, updatedAt: new Date().toISOString() } : t,
        );
        saveJSON(LS_TICKETS, next);
        return next;
      });
    },
    [LS_TICKETS],
  );
  const adoptCanonicalTicket = useCallback(
    (ticket: Ticket) => {
      // A 409 response is a read of the canonical durable winner.
      skipNextWrite.current = true;
      setTickets((prev) => {
        const next = prev.map((current) => (current.id === ticket.id ? ticket : current));
        saveJSON(LS_TICKETS, next);
        return next;
      });
    },
    [LS_TICKETS],
  );
  const attachScreenshot = useCallback(
    (ticket: Ticket) => {
      if (!screenshotUrl || !screenshots) return;
      if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') return;
      // One frame so React has committed (the capture panel / pin composer is closing) before we clone the DOM.
      window.requestAnimationFrame(() => {
        void (async () => {
          try {
            const marker =
              ticket.kind === 'pin' && typeof ticket.x === 'number' && typeof ticket.y === 'number'
                ? surfacePointToClientPoint(ticket.x, ticket.y)
                : undefined;
            const shot = await captureViewport({ marker });
            if (!shot) return;
            const id = await uploadScreenshot(
              screenshotUrl,
              { ...shot, ticketId: ticket.id },
              withAuth(JSON_HEADERS),
            );
            if (id) updateTicket(ticket.id, { screenshotId: id });
          } catch {
            /* The ticket already exists; an attachment is best-effort */
          }
        })();
      });
    },
    [screenshotUrl, screenshots, updateTicket, withAuth],
  );
  attachScreenshotRef.current = attachScreenshot;

  const bulkUpdateTickets = useCallback(
    (ids: string[], patch: Partial<Ticket>) => {
      const idSet = new Set(ids);
      setTickets((prev) => {
        const next = prev.map((t) =>
          idSet.has(t.id) ? { ...t, ...patch, updatedAt: new Date().toISOString() } : t,
        );
        saveJSON(LS_TICKETS, next);
        return next;
      });
    },
    [LS_TICKETS],
  );
  const removeTicket = useCallback(
    (id: string) => {
      setTickets((prev) => {
        const next = prev.filter((t) => t.id !== id);
        saveJSON(LS_TICKETS, next);
        return next;
      });
    },
    [LS_TICKETS],
  );
  const bulkRemoveTickets = useCallback(
    (ids: string[]) => {
      const idSet = new Set(ids);
      setTickets((prev) => {
        const next = prev.filter((t) => !idSet.has(t.id));
        saveJSON(LS_TICKETS, next);
        return next;
      });
    },
    [LS_TICKETS],
  );
  const clearTickets = useCallback(() => {
    setTickets([]);
    saveJSON(LS_TICKETS, []);
  }, [LS_TICKETS]);
  const addComment = useCallback(
    (ticketId: string, comment: { text: string; who?: string }) => {
      setTickets((prev) => {
        const next = prev.map((t) => {
          if (t.id !== ticketId) return t;
          const c = {
            id: newCommentId(),
            at: new Date().toISOString(),
            who: comment.who || reporter || 'anonymous',
            text: comment.text,
          };
          return { ...t, comments: [...(t.comments || []), c], updatedAt: new Date().toISOString() };
        });
        saveJSON(LS_TICKETS, next);
        return next;
      });
    },
    [LS_TICKETS, reporter],
  );

  // ─── cloud sync (optional) ──────────────────────────────────────────
  const skipNextWrite = useRef(false);
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set once a sync request comes back 401 (the board requires auth —).
  const syncDisabledRef = useRef(false);

  const mergeTickets = useCallback((local: Ticket[], remote: Ticket[]): Ticket[] => {
    const map = new Map<string, Ticket>();
    for (const t of remote || []) if (t && t.id) map.set(t.id, t);
    for (const t of local || []) {
      if (!t || !t.id) continue;
      const there = map.get(t.id);
      if (!there) {
        map.set(t.id, t);
        continue;
      }
      const localVersion = t._feedback_version ?? 0;
      const remoteVersion = there._feedback_version ?? 0;
      if (localVersion !== remoteVersion) {
        map.set(t.id, localVersion > remoteVersion ? t : there);
        continue;
      }
      const ta = new Date(t.updatedAt || t.createdAt || 0).getTime();
      const tb = new Date(there.updatedAt || there.createdAt || 0).getTime();
      map.set(t.id, ta > tb ? t : there);
    }
    return filterAudienceTickets([...map.values()]).sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
  }, [filterAudienceTickets]);

  // Initial fetch — adopt remote if any, else push local up
  useEffect(() => {
    if (!syncUrl) return;
    let alive = true;
    setSyncState('loading');
    fetch(syncUrl, { method: 'GET', cache: 'no-store', credentials: 'include', headers: withAuth(CSRF_HEADERS) })
      .then((r) => {
        if (!r.ok) throw new SyncHttpError(r.status);
        return r.json();
      })
      .then(async ({ tickets: remote = [] }: { tickets?: Ticket[] }) => {
        if (!alive) return;
        const local = ticketsRef.current || [];

        const adopt = (server: Ticket[]) => {
          const merged = mergeTickets(local, server);
          skipNextWrite.current = true;
          setTickets(() => {
            saveJSON(LS_TICKETS, merged);
            return merged;
          });
          setSyncState('synced');
          setLastSyncedAt(new Date().toISOString());
          if (merged.length !== server.length) {
            fetch(syncUrl, {
              method: 'PUT',
              credentials: 'include',
              headers: withAuth(JSON_HEADERS),
              body: JSON.stringify(merged),
            }).catch(() => {
              if (alive) setSyncState('offline');
            });
          }
        };

        if (remote.length === 0 && local.length > 0) {
          let confirmed: Ticket[] = [];
          try {
            const res = await fetch(syncUrl, {
              method: 'GET',
              cache: 'no-store',
              credentials: 'include',
              headers: withAuth(CSRF_HEADERS),
            });
            if (!res.ok) throw new SyncHttpError(res.status);
            const body = (await res.json()) as { tickets?: Ticket[] };
            confirmed = body?.tickets ?? [];
          } catch {
            // Could not confirm.
            if (alive) setSyncState('offline');
            return;
          }
          if (!alive) return;
          if (confirmed.length > 0) {
            adopt(confirmed);
            return;
          }
          skipNextWrite.current = false;
          fetch(syncUrl, {
            method: 'PUT',
            credentials: 'include',
            headers: withAuth(JSON_HEADERS),
            body: JSON.stringify(local),
          })
            .then(() => {
              if (alive) {
                setSyncState('synced');
                setLastSyncedAt(new Date().toISOString());
              }
            })
            .catch(() => {
              if (alive) setSyncState('offline');
            });
        } else if (remote.length > 0) {
          adopt(remote);
        } else {
          setSyncState('synced');
          setLastSyncedAt(new Date().toISOString());
        }
      })
      .catch((err) => {
        if (isUnauthorized(err)) syncDisabledRef.current = true;
        if (alive) setSyncState('offline');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncUrl, withAuth]);

  // Debounced write on change
  useEffect(() => {
    if (!syncUrl || syncDisabledRef.current) return;
    if (skipNextWrite.current) {
      skipNextWrite.current = false;
      return;
    }
    if (syncStateRef.current === 'idle' || syncStateRef.current === 'loading') return;
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(() => {
      setSyncState((s) => (s === 'offline' ? s : 'syncing'));
      fetch(syncUrl, {
        method: 'PUT',
        credentials: 'include',
        headers: withAuth(JSON_HEADERS),
        body: JSON.stringify(ticketsRef.current),
      })
        .then((r) => {
          if (!r.ok) throw new SyncHttpError(r.status);
          return r.json();
        })
        .then((body: { tickets?: Ticket[] }) => {
          const canonical = body?.tickets;
          if (canonical) {
            skipNextWrite.current = true;
            setTickets(() => {
              const next = filterAudienceTickets(canonical);
              saveJSON(LS_TICKETS, next);
              return next;
            });
          }
          setSyncState('synced');
          setLastSyncedAt(new Date().toISOString());
        })
        .catch((err) => {
          if (isUnauthorized(err)) syncDisabledRef.current = true;
          setSyncState('offline');
        });
    }, SYNC_DEBOUNCE_MS);
    return () => {
      if (writeTimer.current) clearTimeout(writeTimer.current);
    };
    // syncState intentionally NOT a dep — see syncStateRef above (loop guard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, syncUrl, withAuth, filterAudienceTickets, LS_TICKETS]);

  // Poll for other reviewers' changes
  useEffect(() => {
    if (!syncUrl) return;
    const id = setInterval(() => {
      if (syncDisabledRef.current) return;
      if (document.visibilityState === 'hidden') return;
      fetch(syncUrl, { method: 'GET', cache: 'no-store', credentials: 'include', headers: withAuth(CSRF_HEADERS) })
        .then((r) => (r.ok ? r.json() : Promise.reject(new SyncHttpError(r.status))))
        .then(({ tickets: remote = [] }: { tickets?: Ticket[] }) => {
          const local = ticketsRef.current;
          if (remote.length === local.length) {
            const same = remote.every(
              (t, i) =>
                t && local[i] && t.id === local[i].id && (t.updatedAt || '') === (local[i].updatedAt || ''),
            );
            if (same) return;
          }
          const merged = mergeTickets(local, remote);
          const noNew =
            merged.length === local.length &&
            merged.every(
              (t, i) => t.id === local[i].id && (t.updatedAt || '') === (local[i].updatedAt || ''),
            );
          if (noNew) return;
          skipNextWrite.current = true;
          setTickets(() => {
            saveJSON(LS_TICKETS, merged);
            return merged;
          });
          setSyncState('synced');
          setLastSyncedAt(new Date().toISOString());
        })
        .catch((err) => {
          if (isUnauthorized(err)) syncDisabledRef.current = true;
          setSyncState((s) => (s === 'syncing' ? s : 'offline'));
        });
    }, SYNC_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncUrl, mergeTickets, withAuth]);

  const forceSyncNow = useCallback(() => {
    if (!syncUrl) return;
    // A manual retry re-enables sync (e.g. the reviewer just signed in); a fresh 401 below will disable it again.
    syncDisabledRef.current = false;
    setSyncState('syncing');
    fetch(syncUrl, { method: 'GET', cache: 'no-store', credentials: 'include', headers: withAuth(CSRF_HEADERS) })
      .then((r) => (r.ok ? r.json() : Promise.reject(new SyncHttpError(r.status))))
      .then(({ tickets: remote = [] }: { tickets?: Ticket[] }) => {
        const merged = mergeTickets(ticketsRef.current, remote);
        skipNextWrite.current = true;
        setTickets(() => {
          saveJSON(LS_TICKETS, merged);
          return merged;
        });
        return fetch(syncUrl, {
          method: 'PUT',
          credentials: 'include',
          headers: withAuth(JSON_HEADERS),
          body: JSON.stringify(merged),
        });
      })
      .then((r) => (r.ok ? r.json() : Promise.reject(new SyncHttpError(r.status))))
      .then(() => {
        setSyncState('synced');
        setLastSyncedAt(new Date().toISOString());
      })
      .catch((err) => {
        if (isUnauthorized(err)) syncDisabledRef.current = true;
        setSyncState('offline');
      });
  }, [syncUrl, mergeTickets, LS_TICKETS, withAuth]);

  useEffect(() => {
    if (!autoCapture) return;
    const onError = (e: ErrorEvent) => {
      const msg = e?.message || 'Unknown error';
      const file = e?.filename || '';
      const line = e?.lineno || '';
      const col = e?.colno || '';
      const stack = e?.error?.stack || '';
      const stackKey = stack.split('\n').slice(0, 2).join('|');
      const { route: r } = routeRef.current;
      addAutoTicket({
        tag: 'bug',
        severity: 'high',
        source: 'auto-error',
        title: msg.slice(0, 80),
        note: `${msg}\n\nat ${file}:${line}:${col}\n\n${stack || '(no stack)'}`,
        dedupeKey: `err:${msg}|${stackKey}|${r}`,
      });
    };
    const onReject = (e: PromiseRejectionEvent) => {
      const reason = e?.reason;
      const msg =
        (reason && (reason.message || String(reason))) || 'Unhandled promise rejection';
      const stack = (reason && reason.stack) || '';
      const stackKey = stack.split('\n').slice(0, 2).join('|');
      const { route: r } = routeRef.current;
      addAutoTicket({
        tag: 'bug',
        severity: 'high',
        source: 'auto-rejection',
        title: msg.slice(0, 80),
        note: `Unhandled promise rejection:\n${msg}\n\n${stack || '(no stack)'}`,
        dedupeKey: `rej:${msg}|${stackKey}|${r}`,
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onReject);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onReject);
    };
  }, [addAutoTicket, autoCapture]);

  useEffect(() => {
    if (!autoCapture) return;

    const ACTIVITY_ATTRIBUTION_MS = 50;
    interface PendingClick {
      expiresAt: number;
      sawActivity: boolean;
    }
    const pendingClicks = new Set<PendingClick>();
    const markActivity = () => {
      const now = Date.now();
      for (const rec of pendingClicks) {
        if (now <= rec.expiresAt) rec.sawActivity = true;
      }
    };

    const origFetch = window.fetch?.bind(window);
    if (origFetch) {
      window.fetch = ((...args: Parameters<typeof fetch>) => {
        markActivity();
        return origFetch(...args);
      }) as typeof window.fetch;
    }

    const origXhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function send(
      this: XMLHttpRequest,
      ...args: Parameters<typeof XMLHttpRequest.prototype.send>
    ) {
      markActivity();
      return origXhrSend.apply(this, args);
    };

    const origOpen = window.open?.bind(window);
    if (origOpen) {
      window.open = ((...args: Parameters<typeof window.open>) => {
        markActivity();
        return origOpen(...args);
      }) as typeof window.open;
    }

    const origDispatchEvent = window.dispatchEvent?.bind(window);
    if (origDispatchEvent) {
      window.dispatchEvent = ((event: Event) => {
        if (event instanceof CustomEvent) markActivity();
        return origDispatchEvent(event);
      }) as typeof window.dispatchEvent;
    }

    const origCreateObjectURL = URL.createObjectURL?.bind(URL);
    if (origCreateObjectURL) {
      URL.createObjectURL = ((...args: Parameters<typeof URL.createObjectURL>) => {
        markActivity();
        return origCreateObjectURL(...args);
      }) as typeof URL.createObjectURL;
    }

    let unloadingAt = 0;
    const onUnloadSignal = () => {
      unloadingAt = Date.now();
    };
    window.addEventListener('beforeunload', onUnloadSignal);
    window.addEventListener('pagehide', onUnloadSignal);

    const MUTATION_OPTS: MutationObserverInit = {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    };
    const CONTAINER_SELECTOR = 'li, tr, form, [role="listitem"], [class*="card"], [class*="row"]';
    const CONTAINER_MAX_DEPTH = 4;
    function nearestContainer(hit: HTMLElement): HTMLElement | null {
      let el: HTMLElement | null = hit.parentElement;
      let depth = 0;
      while (el && el !== document.body && depth < CONTAINER_MAX_DEPTH) {
        if (el.matches?.(CONTAINER_SELECTOR)) return el;
        el = el.parentElement;
        depth++;
      }
      return hit.parentElement;
    }

    const OVERLAY_SELECTOR =
      'dialog[open], [role="dialog"], [role="alertdialog"], [aria-modal="true"], [role="alert"], [role="status"], [role="tooltip"], [role="menu"], [role="listbox"]';
    const ACTIVE_ATTRS = ['aria-pressed', 'aria-selected', 'aria-checked', 'aria-current'];
    const ACTIVE_CLASS_RE = /(^|\s)(selected|is-selected|is-active|active|checked)(\s|$)/;
    // Read BEFORE the click reaches the app's own handler (this listener is capture-phase).
    function isAlreadyActive(hit: HTMLElement): boolean {
      for (const attr of ACTIVE_ATTRS) {
        if (hit.getAttribute(attr) === 'true') return true;
      }
      const cls = typeof hit.className === 'string' ? hit.className : '';
      return ACTIVE_CLASS_RE.test(cls);
    }

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Ignore anything inside the toolbar itself
      if (target.closest('.fbtk-root')) return;

      if (target.closest('a[download]')) markActivity();

      if (target.closest('input[type="file"]')) markActivity();

      let el: HTMLElement | null = target;
      let hit: HTMLElement | null = null;
      while (el && el !== document.body) {
        const tag = el.tagName.toLowerCase();
        if (tag === 'button' || tag === 'a' || el.getAttribute('role') === 'button') {
          hit = el;
          break;
        }
        el = el.parentElement;
      }
      if (!hit) return;
      // Links that clearly navigate are not "dead"
      if (hit.tagName.toLowerCase() === 'a') {
        const href = hit.getAttribute('href');
        if (href && href !== '#' && !href.startsWith('javascript:')) return;
      }
      // inputs/labels that toggle form controls are not "dead"
      if (hit.tagName.toLowerCase() === 'button' && (hit as HTMLButtonElement).type === 'submit') return;
      // Idempotent re-click of an already-active control: expected no-op
      if (isAlreadyActive(hit)) return;

      const clickAt = Date.now();
      const overlayCount = () => document.querySelectorAll(OVERLAY_SELECTOR).length;
      const overlaysBefore = overlayCount();
      // Portals mount as new direct children of <body>.
      let portalMounted = false;
      const bodyMo = new MutationObserver((records) => {
        // Treat body-level portals like click evidence only when they are attributable to this click.
        if (Date.now() > clickAt + ACTIVITY_ATTRIBUTION_MS) return;
        for (const rec of records) {
          if (rec.addedNodes.length > 0) portalMounted = true;
        }
      });
      bodyMo.observe(document.body, { childList: true });
      const focusBefore = document.activeElement;
      const scrollBefore = `${window.scrollX},${window.scrollY}`;

      const urlBefore = window.location.href;
      const label = (hit.getAttribute('aria-label') || hit.textContent || '').trim().slice(0, 60);
      const hitTag = hit.tagName.toLowerCase();
      const hitClass = hit.className && typeof hit.className === 'string' ? hit.className : '';

      let mutated = false;
      const scopedMo = new MutationObserver(() => {
        mutated = true;
      });
      scopedMo.observe(hit, MUTATION_OPTS);
      const container = nearestContainer(hit);
      if (container && container !== hit) scopedMo.observe(container, MUTATION_OPTS);

      const activityToken: PendingClick = { expiresAt: clickAt + ACTIVITY_ATTRIBUTION_MS, sawActivity: false };
      pendingClicks.add(activityToken);

      window.setTimeout(() => {
        scopedMo.disconnect();
        pendingClicks.delete(activityToken);
        const navigated = window.location.href !== urlBefore;
        const navigatingAway = unloadingAt >= clickAt;
        const didActivity = activityToken.sawActivity;
        const controlRemoved = !document.contains(hit);
        bodyMo.disconnect();
        const overlayOpened = overlayCount() > overlaysBefore;
        const focusMoved =
          document.activeElement !== focusBefore &&
          document.activeElement !== null &&
          document.activeElement !== document.body &&
          !hit.contains(document.activeElement);
        const scrolled = `${window.scrollX},${window.scrollY}` !== scrollBefore;
        if (
          navigated ||
          navigatingAway ||
          mutated ||
          didActivity ||
          controlRemoved ||
          overlayOpened ||
          portalMounted ||
          focusMoved ||
          scrolled
        )
          return; // it did something
        const { route: r, routeLabel: rl } = routeRef.current;
        addAutoTicket({
          tag: 'bug',
          severity: 'low',
          source: 'auto-deadclick',
          title: `Dead click · ${label ? `"${label}"` : 'unlabeled element'}`,
          note: `Clicked an interactive element on ${rl} (${r}) that produced no navigation or DOM change.\n\nLabel: ${label || '(none)'}\nElement: <${hitTag}${hitClass ? ` class="${hitClass}"` : ''}>`,
          dedupeKey: `dead:${r}:${label.toLowerCase()}`,
        });
      }, DEAD_CLICK_MS);
    };
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('beforeunload', onUnloadSignal);
      window.removeEventListener('pagehide', onUnloadSignal);
      if (origFetch) window.fetch = origFetch;
      XMLHttpRequest.prototype.send = origXhrSend;
      if (origOpen) window.open = origOpen;
      if (origCreateObjectURL) URL.createObjectURL = origCreateObjectURL;
      if (origDispatchEvent) window.dispatchEvent = origDispatchEvent;
    };
  }, [addAutoTicket, autoCapture]);

  const value = useMemo<FeedbackContextValue>(
    () => ({
      tickets,
      addTicket,
      addAutoTicket,
      updateTicket,
      adoptCanonicalTicket,
      bulkUpdateTickets,
      removeTicket,
      bulkRemoveTickets,
      clearTickets,
      addComment,
      reporter,
      setReporter,
      autoCapture,
      setAutoCapture,
      screenshots,
      setScreenshots,
      screenshotUrl: screenshotUrl ?? null,
      authHeaders: withAuth,
      commentMode,
      setCommentMode,
      fbOpen,
      setFbOpen,
      dashOpen,
      setDashOpen,
      toasts,
      pushToast,
      dismissToast,
      syncEnabled,
      syncUrl: syncUrl ?? null,
      isZoeAdmin,
      syncState,
      lastSyncedAt,
      forceSyncNow,
      route,
      routeLabel,
    }),
    [
      tickets,
      addTicket,
      addAutoTicket,
      updateTicket,
      adoptCanonicalTicket,
      bulkUpdateTickets,
      removeTicket,
      bulkRemoveTickets,
      clearTickets,
      addComment,
      reporter,
      setReporter,
      autoCapture,
      setAutoCapture,
      screenshots,
      setScreenshots,
      screenshotUrl,
      withAuth,
      commentMode,
      fbOpen,
      dashOpen,
      toasts,
      pushToast,
      dismissToast,
      syncEnabled,
      syncUrl,
      isZoeAdmin,
      syncState,
      lastSyncedAt,
      forceSyncNow,
      route,
      routeLabel,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFeedback(): FeedbackContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useFeedback must be used within <FeedbackToolbar>');
  return ctx;
}
